/**
 * NegoTable — Tableau des négociations + ligne total.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, MoreHorizontal, Inbox } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  fmtXof, fmtRelative, STATUS_META, derivePriority, slaInfo,
  initialOffer, negotiatedOffer, offerGap, TERMINAL_STATUSES,
} from './negoMeta';

const useTick = (ms = 30000) => {
  const [, set] = useState(0);
  useEffect(() => { const id = setInterval(() => set((t) => t + 1), ms); return () => clearInterval(id); }, [ms]);
};

const Avatar = ({ name }) => {
  if (!name) return <span className="text-xs text-slate-400">Non assigné</span>;
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-700 text-[10px] font-bold text-white">{initials}</div>
      <span className="truncate text-sm text-slate-700">{name}</span>
    </div>
  );
};

const Badge = ({ cls, children, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${cls}`}>{children}</span>
);

const GapCell = ({ gap }) => {
  if (gap == null) return <span className="text-slate-400">—</span>;
  const cls = gap < 0 ? 'text-rose-600' : gap > 0 ? 'text-emerald-600' : 'text-slate-600';
  return <span className={`font-semibold tabular-nums ${cls}`}>{gap > 0 ? '+' : ''}{fmtXof(gap)}</span>;
};

const Row = ({ nego, selected, onSelect, onOpen, onAction }) => {
  const status = STATUS_META[nego.status] || { label: nego.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  const prio = derivePriority(nego);
  const sla = slaInfo(nego);
  const init = initialOffer(nego);
  const neg = negotiatedOffer(nego);
  const gap = offerGap(nego);
  const responsable = nego.assignedSalesName || nego.decisionOwnerName || nego.createdByName;
  return (
    <tr
      data-testid={`nego-row-${nego.id}`}
      onClick={() => onOpen(nego.id)}
      className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50"
    >
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={() => onSelect(nego.id)} data-testid={`nego-select-${nego.id}`} />
      </td>
      <td className="px-3 py-3"><Badge cls={prio.cls} testId={`nego-prio-${nego.id}`}>{prio.label}</Badge></td>
      <td className="px-3 py-3">
        <div className="font-mono text-sm font-bold text-slate-900">{nego.operationNumber || '—'}</div>
        <div className="text-xs text-slate-500">{nego.clientName || '—'}</div>
        {nego.departureCity && <div className="text-xs text-slate-400">{nego.departureCity} → {nego.arrivalCity || '—'}</div>}
      </td>
      <td className="px-3 py-3 text-sm tabular-nums text-slate-700">{init != null ? fmtXof(init) : '—'}</td>
      <td className="px-3 py-3 text-sm font-medium tabular-nums text-slate-900">{neg != null ? fmtXof(neg) : '—'}</td>
      <td className="px-3 py-3 text-sm"><GapCell gap={gap} /></td>
      <td className="px-3 py-3 text-sm text-slate-400">—</td>
      <td className="px-3 py-3 w-40"><Avatar name={responsable} /></td>
      <td className="px-3 py-3">
        {sla.remainingMin == null ? <span className="text-xs text-slate-400">—</span> : (
          <div className={`text-xs ${sla.cls}`} data-testid={`nego-sla-${nego.id}`}>
            <div className="font-semibold">{sla.label.replace(' restantes', '')}</div>
            {!sla.overdue && <div className="text-[10px] text-slate-400">restantes</div>}
          </div>
        )}
      </td>
      <td className="px-3 py-3"><Badge cls={status.cls} testId={`nego-status-${nego.id}`}>{status.label}</Badge></td>
      <td className="px-3 py-3 text-xs text-slate-500">{fmtRelative(nego.lastActivityAt || nego.createdAt)}</td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`nego-actions-${nego.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onOpen(nego.id)}>Voir le détail</DropdownMenuItem>
            {!TERMINAL_STATUSES.includes(nego.status) && (
              <>
                <DropdownMenuItem onClick={() => onAction(nego, 'accept')}>Accepter</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction(nego, 'reject')}>Refuser</DropdownMenuItem>
                {nego.status !== 'countered' && <DropdownMenuItem onClick={() => onOpen(nego.id)}>Contre-offre</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => onAction(nego, 'escalate')}>Escalader</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

const TOTAL_COLS = 12;

export const NegoTable = ({ rows, loading, selected, onSelect, onSelectAll, onOpen, onAction, emptyTab, onSeeAll, onCreate }) => {
  useTick(30000);
  const totalInit = rows.reduce((a, n) => a + (initialOffer(n) || 0), 0);
  const totalNeg = rows.reduce((a, n) => a + (negotiatedOffer(n) || 0), 0);
  const totalGap = rows.reduce((a, n) => a + (offerGap(n) || 0), 0);
  const allSelected = rows.length > 0 && selected.size === rows.length;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="nego-table-wrap">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-3 py-3"><Checkbox checked={allSelected} onCheckedChange={onSelectAll} data-testid="nego-select-all" /></th>
              <th className="px-3 py-3">Priorité</th>
              <th className="px-3 py-3">Opération · Client · Trajet</th>
              <th className="px-3 py-3">Offre initiale</th>
              <th className="px-3 py-3">Offre négociée</th>
              <th className="px-3 py-3">Écart</th>
              <th className="px-3 py-3">Marge</th>
              <th className="px-3 py-3">Responsable</th>
              <th className="px-3 py-3">SLA</th>
              <th className="px-3 py-3">Statut</th>
              <th className="px-3 py-3">Dernière activité</th>
              <th className="px-3 py-3">Action</th>
            </tr>
          </thead>
          <tbody data-testid="nego-table-body">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td colSpan={TOTAL_COLS} className="px-3 py-4"><div className="h-6 animate-pulse rounded bg-slate-100" /></td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={TOTAL_COLS} className="px-6 py-16 text-center" data-testid="nego-empty-state">
                  <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50">
                    {emptyTab ? <CheckCircle2 className="h-8 w-8 text-emerald-600" /> : <Inbox className="h-8 w-8 text-emerald-600" />}
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">Aucune négociation</h3>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">Toutes les demandes sont à jour pour ce filtre.</p>
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <button onClick={onSeeAll} className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="empty-see-all">Voir toutes les négociations</button>
                    <button onClick={onCreate} className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="empty-create">Créer une négociation</button>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((nego) => (
                <Row key={nego.id} nego={nego} selected={selected.has(nego.id)} onSelect={onSelect} onOpen={onOpen} onAction={onAction} />
              ))
            )}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-emerald-50/40 text-sm font-bold text-slate-800" data-testid="nego-total-row">
                <td className="px-3 py-3"><CheckCircle2 className="h-4 w-4 text-emerald-600" /></td>
                <td className="px-3 py-3 text-emerald-700">Total</td>
                <td className="px-3 py-3 text-slate-600">{rows.length} dossier{rows.length > 1 ? 's' : ''}</td>
                <td className="px-3 py-3 tabular-nums text-emerald-700">{fmtXof(totalInit)}</td>
                <td className="px-3 py-3 tabular-nums text-emerald-700">{fmtXof(totalNeg)}</td>
                <td className={`px-3 py-3 tabular-nums ${totalGap < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{totalGap > 0 ? '+' : ''}{fmtXof(totalGap)}</td>
                <td className="px-3 py-3 text-slate-400">—</td>
                <td className="px-3 py-3" colSpan={5}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default NegoTable;
